import { useMemo, useState } from "react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { toast } from "sonner"
import { useCreateMaterial, useCreateCategory } from "@/lib/api/hooks"
import type { Category } from "@/types/inventory"
import { CategoryDialog } from "./CategoryDialog"
import { StepIndicator } from "@/components/ui/step-indicator"
import { StepContainer } from "@/components/ui/step-container"

interface AddMaterialDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  categories: Category[]
}

const UNIT_OPTIONS = [
  { value: "Stück", label: "Stück" },
  { value: "Meter", label: "Meter" },
  { value: "Kilogramm", label: "Kilogramm" },
  { value: "Liter", label: "Liter" },
  { value: "Packung", label: "Packung" },
]

function parseMoney(value: string): number | null {
  const normalized = value.replace(",", ".").trim()
  if (!normalized) return null
  const parsed = Number(normalized)
  if (!Number.isFinite(parsed) || parsed < 0) return null
  return Math.round(parsed * 100)
}

function parseNonNegativeInteger(value: string): number | null {
  const trimmed = value.trim()
  if (!trimmed) return null
  const parsed = Number(trimmed)
  if (!Number.isInteger(parsed) || parsed < 0) return null
  return parsed
}

export function AddMaterialDialog({
  open,
  onOpenChange,
  categories,
}: AddMaterialDialogProps) {
  const [categoryId, setCategoryId] = useState("")
  const [name, setName] = useState("")
  const [quantity, setQuantity] = useState("")
  const [unit, setUnit] = useState("")
  const [minQuantity, setMinQuantity] = useState("")
  const [location, setLocation] = useState("")
  const [basePrice, setBasePrice] = useState("")
  const [priceMarkupPercentage, setPriceMarkupPercentage] = useState("")
  const [expiresOn, setExpiresOn] = useState("")
  const [batchCode, setBatchCode] = useState("")
  const [isCategoryDialogOpen, setIsCategoryDialogOpen] = useState(false)
  const [currentStep, setCurrentStep] = useState(1)

  const createMaterial = useCreateMaterial()
  const createCategory = useCreateCategory()

  const selectedCategory = useMemo(
    () => categories.find((category) => category.id === categoryId) ?? null,
    [categories, categoryId]
  )

  const quantityValue = Number(quantity) || 0
  const requiresExpiryDate = selectedCategory?.can_expire === true && quantityValue > 0
  const parsedBasePrice = parseMoney(basePrice)
  const parsedMarkupPercentage = parseNonNegativeInteger(priceMarkupPercentage)

  const isStep1Valid = Boolean(categoryId && name && quantity && unit)
  const isStep2Valid = Boolean(
    minQuantity &&
      (!requiresExpiryDate || expiresOn) &&
      (basePrice.trim() === "" || parsedBasePrice != null) &&
      (priceMarkupPercentage.trim() === "" || parsedMarkupPercentage != null)
  )

  const resetForm = () => {
    setCategoryId("")
    setName("")
    setQuantity("")
    setUnit("")
    setMinQuantity("")
    setLocation("")
    setBasePrice("")
    setPriceMarkupPercentage("")
    setExpiresOn("")
    setBatchCode("")
    setIsCategoryDialogOpen(false)
    setCurrentStep(1)
  }

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      resetForm()
    }
    onOpenChange(nextOpen)
  }

  const isFormValid = isStep1Valid && isStep2Valid

  const handleSubmit = () => {
    if (!isFormValid) {
      return
    }

    createMaterial.mutate(
      {
        category_id: categoryId,
        name,
        description: null,
        quantity: Number(quantity),
        unit,
        min_quantity: Number(minQuantity),
        location: location || null,
        base_price_cents: parsedBasePrice,
        price_markup_percentage: parsedMarkupPercentage,
        expires_on: expiresOn || null,
        batch_code: batchCode || null,
      },
      {
        onSuccess: () => {
          toast.success("Material erstellt")
          handleOpenChange(false)
        },
        onError: (error) => {
          toast.error("Material konnte nicht erstellt werden")
          console.error("Create material error:", error)
        },
      }
    )
  }

  const handleCreateCategory = (values: {
    name: string
    description: string
    canExpire: boolean
  }) => {
    createCategory.mutate(
      {
        name: values.name,
        description: values.description || null,
        can_expire: values.canExpire,
      },
      {
        onSuccess: (newCategory) => {
          setCategoryId(newCategory.id)
          setIsCategoryDialogOpen(false)
          toast.success("Kategorie erstellt")
        },
      }
    )
  }

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-md max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Material hinzufügen</DialogTitle>
            <DialogDescription>Neues Material zum Inventar hinzufügen</DialogDescription>
          </DialogHeader>

          <StepIndicator
            currentStep={currentStep}
            totalSteps={2}
            onStepClick={(step) => {
              if (step === 1 || isStep1Valid) {
                setCurrentStep(step)
              }
            }}
          />

          <StepContainer
            currentStep={currentStep}
            onStepChange={setCurrentStep}
            totalSteps={2}
          >
            <div className="space-y-4 py-4 overflow-y-auto flex-1">
              {currentStep === 1 ? (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="category">Kategorie *</Label>
                    <div className="flex gap-2">
                      <Select value={categoryId} onValueChange={setCategoryId}>
                        <SelectTrigger id="category" className="flex-1">
                          <SelectValue placeholder="Kategorie wählen" />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map((category) => (
                            <SelectItem key={category.id} value={category.id}>
                              {category.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setIsCategoryDialogOpen(true)}
                      >
                        + Neu
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      placeholder="z.B. Schrauben M8"
                      value={name}
                      onChange={(event) => setName(event.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="quantity">Menge *</Label>
                    <Input
                      id="quantity"
                      type="number"
                      min={0}
                      step="1"
                      placeholder="0"
                      value={quantity}
                      onChange={(event) => setQuantity(event.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="unit">Einheit *</Label>
                    <Select value={unit} onValueChange={setUnit}>
                      <SelectTrigger id="unit">
                        <SelectValue placeholder="Einheit wählen" />
                      </SelectTrigger>
                      <SelectContent>
                        {UNIT_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="minQuantity">Mindestbestand *</Label>
                    <Input
                      id="minQuantity"
                      type="number"
                      min={0}
                      step="1"
                      placeholder="0"
                      value={minQuantity}
                      onChange={(event) => setMinQuantity(event.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="basePrice">Basispreis (EUR)</Label>
                    <Input
                      id="basePrice"
                      type="number"
                      min={0}
                      step="0.01"
                      inputMode="decimal"
                      placeholder="z.B. 12,50"
                      value={basePrice}
                      onChange={(event) => setBasePrice(event.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="priceMarkupPercentage">Aufschlag (%)</Label>
                    <Input
                      id="priceMarkupPercentage"
                      type="number"
                      min={0}
                      step="1"
                      inputMode="numeric"
                      placeholder="z.B. 15"
                      value={priceMarkupPercentage}
                      onChange={(event) => setPriceMarkupPercentage(event.target.value)}
                    />
                    <p className="text-sm text-muted-foreground">
                      Wird als Standardaufschlag für die Kundenrechnung verwendet.
                    </p>
                  </div>

                  {selectedCategory?.can_expire && (
                    <div className="space-y-4 rounded-xl border border-border/70 bg-card/70 p-4 shadow-sm">
                      <div className="space-y-2">
                        <Label htmlFor="expiresOn" title="Mindesthaltbarkeitsdatum">
                          MHD *
                        </Label>
                        <Input
                          id="expiresOn"
                          type="date"
                          value={expiresOn}
                          onChange={(event) => setExpiresOn(event.target.value)}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="batchCode">Charge / Los</Label>
                        <Input
                          id="batchCode"
                          placeholder="optional, z. B. LOT-2026-05"
                          value={batchCode}
                          onChange={(event) => setBatchCode(event.target.value)}
                        />
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="location">Lagerort</Label>
                    <Input
                      id="location"
                      placeholder="z.B. Regal A3"
                      value={location}
                      onChange={(event) => setLocation(event.target.value)}
                    />
                  </div>
                </>
              )}
            </div>
          </StepContainer>

          <DialogFooter>
            {currentStep === 1 ? (
              <>
                <Button variant="outline" onClick={() => handleOpenChange(false)}>
                  Abbrechen
                </Button>
                <Button
                  onClick={() => setCurrentStep(2)}
                  disabled={!isStep1Valid}
                >
                  Weiter
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => setCurrentStep(1)}>
                  Zurück
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={!isStep2Valid || createMaterial.isPending}
                  className="shadow-sm active:scale-[0.97] transition-transform"
                >
                  {createMaterial.isPending ? "Wird erstellt..." : "Erstellen"}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CategoryDialog
        open={isCategoryDialogOpen}
        onOpenChange={setIsCategoryDialogOpen}
        title="Kategorie anlegen"
        description="Legen Sie direkt beim Materialanlegen eine neue Kategorie an."
        submitLabel="Kategorie erstellen"
        isSubmitting={createCategory.isPending}
        onSubmit={handleCreateCategory}
      />
    </>
  )
}
